// Staff books for a phone/in-shop customer, or records a walk-in starting now.
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client.ts';
import type { CustomerRow } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { StaffTabs, useShopPaths } from '../app/nav.tsx';
import { fieldErrors, message } from '../lib/errors.ts';
import { todayLocal } from '../lib/time.ts';
import { Banner, Button, Card, Field, Page, Segmented, useToast } from '../ui/index.tsx';
import { NeedsConfirmDialog, type SlotChoice, StaffSlotPicker } from './pickers.tsx';

type Mode = 'customer' | 'walkin';
type Flags = { confirmOutsideHours: boolean; confirmTimeOff: boolean };

export function StaffBook() {
  const { api, shop, me, isOwner } = useShop();
  const { go } = useShopPaths();
  const toast = useToast();
  const ownBarber = me.staff?.role === 'barber' ? me.staff.barberId : null;

  const [mode, setMode] = useState<Mode>('customer');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CustomerRow[]>([]);
  const [customer, setCustomer] = useState<CustomerRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newErrors, setNewErrors] = useState<Record<string, string>>({});
  const [guestName, setGuestName] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [walkBarber, setWalkBarber] = useState('');
  const [startNow, setStartNow] = useState(true);
  const [choice, setChoice] = useState<SlotChoice | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needs, setNeeds] = useState<{ outsideHours?: boolean; timeOff?: boolean } | null>(null);
  const keyRef = useRef<string | null>(null);

  const service = shop.services.find((s) => s.id === serviceId) ?? null;
  const barberIds = service ? service.barberIds : [];

  useEffect(() => {
    const term = q.trim();
    if (term === '') { setResults([]); return; }
    const t = setTimeout(() => {
      api.staff.customers(term).then(setResults).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, api]);

  useEffect(() => {
    if (ownBarber) setWalkBarber(ownBarber);
    else if (service && !service.barberIds.includes(walkBarber)) setWalkBarber(service.barberIds[0] ?? '');
  }, [service, ownBarber, walkBarber]);

  async function addCustomer() {
    setNewErrors({});
    try {
      const c = await api.staff.createCustomer({ fullName: newName, phone: newPhone.trim() || undefined });
      setCustomer(c);
      setAdding(false);
      setNewName('');
      setNewPhone('');
    } catch (e) {
      const f = fieldErrors(e);
      if (Object.keys(f).length) setNewErrors(f);
      else toast(message(e), 'error');
    }
  }

  const walkInNow = mode === 'walkin' && startNow;
  const target: SlotChoice | null = walkInNow
    ? (walkBarber ? { barberId: walkBarber, startAt: new Date(Math.floor(Date.now() / 60_000) * 60_000).toISOString() } : null)
    : choice;
  const ready = !!service && !!target && (mode === 'walkin' || customer !== null);

  async function submit(flags: Flags = { confirmOutsideHours: false, confirmTimeOff: false }) {
    if (!service || !target) return;
    setBusy(true);
    setError(null);
    keyRef.current ??= crypto.randomUUID();
    const startAt = walkInNow ? new Date(Math.floor(Date.now() / 60_000) * 60_000).toISOString() : target.startAt;
    try {
      await api.staff.createBooking({
        customerId: customer?.id ?? null,
        guestName: mode === 'walkin' && !customer ? guestName.trim() || undefined : undefined,
        walkIn: mode === 'walkin',
        barberId: target.barberId,
        serviceId: service.id,
        startAt,
        note: note.trim() || undefined,
        idempotencyKey: keyRef.current,
        ...flags,
      });
      keyRef.current = null;
      toast('บันทึกการจองแล้ว', 'success');
      go('/staff/today');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'NEEDS_CONFIRM') setNeeds((e.details ?? {}) as { outsideHours?: boolean; timeOff?: boolean });
      else setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  const barberName = (id: string) => shop.barbers.find((b) => b.id === id)?.name ?? id;

  return (
    <>
      <Page title="จองให้ลูกค้า">
        <Segmented label="ประเภท" value={mode} onChange={(v) => { setMode(v); setChoice(null); }}
          options={[{ value: 'customer', label: 'ลูกค้า' }, { value: 'walkin', label: 'Walk-in' }]} />
        <div style={{ height: 16 }} />
        {error && <Banner kind="error">{error}</Banner>}

        {mode === 'customer' && (
          customer ? (
            <Card className="row-between">
              <div>
                <div className="card-title">{customer.fullName}</div>
                <div className="muted small">{customer.phone ?? 'ไม่มีเบอร์'}{customer.lineUserId ? ' · มี LINE' : ''}</div>
              </div>
              <Button variant="ghost" className="btn-sm" onClick={() => setCustomer(null)}>เปลี่ยน</Button>
            </Card>
          ) : adding ? (
            <Card>
              <Field label="ชื่อลูกค้าใหม่" error={newErrors.name}>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={60} />
              </Field>
              <Field label="เบอร์โทร (ถ้ามี)" error={newErrors.phone}>
                <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} inputMode="tel" maxLength={20} />
              </Field>
              <div className="row">
                <Button variant="ghost" onClick={() => setAdding(false)}>ยกเลิก</Button>
                <Button onClick={() => void addCustomer()}>บันทึกลูกค้า</Button>
              </div>
            </Card>
          ) : (
            <div>
              <Field label="ค้นหาลูกค้า (ชื่อหรือเบอร์)">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="เช่น สมชาย หรือ 081" />
              </Field>
              <div className="list" style={{ marginBottom: 12 }}>
                {results.map((c) => (
                  <Card key={c.id} as="button" onClick={() => setCustomer(c)}>
                    <div className="card-title">{c.fullName}</div>
                    <div className="muted small">{c.phone ?? 'ไม่มีเบอร์'}{c.blocked ? ' · ถูกระงับการจองออนไลน์' : ''}</div>
                  </Card>
                ))}
                {q.trim() !== '' && results.length === 0 && <p className="muted small">ไม่พบลูกค้า</p>}
              </div>
              <Button variant="secondary" block onClick={() => { setAdding(true); setNewName(q); }}>เพิ่มลูกค้าใหม่</Button>
            </div>
          )
        )}

        {mode === 'walkin' && (
          <Field label="ชื่อลูกค้า (ถ้ามี)">
            <input value={guestName} onChange={(e) => setGuestName(e.target.value)} maxLength={50} placeholder="Walk-in" />
          </Field>
        )}

        <div style={{ height: 8 }} />
        <Field label="บริการ">
          <select value={serviceId} onChange={(e) => { setServiceId(e.target.value); setChoice(null); }}>
            <option value="">เลือกบริการ</option>
            {shop.services.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.durationMin} นาที · {s.price} บาท</option>)}
          </select>
        </Field>

        {service && mode === 'walkin' && (
          <>
            {!ownBarber && (
              <Field label="ช่าง">
                <select value={walkBarber} onChange={(e) => setWalkBarber(e.target.value)}>
                  {barberIds.map((id) => <option key={id} value={id}>{barberName(id)}</option>)}
                </select>
              </Field>
            )}
            <label className="check">
              <input type="checkbox" checked={startNow} onChange={(e) => setStartNow(e.target.checked)} />
              <span>เริ่มตอนนี้</span>
            </label>
          </>
        )}

        {service && (mode === 'customer' ? customer !== null : !startNow) && (
          <StaffSlotPicker key={`${service.id}-${mode}`} api={api} shop={shop} serviceId={service.id} barberIds={barberIds}
            fixedBarberId={ownBarber} initialDate={todayLocal()} onChange={setChoice} />
        )}

        {service && (
          <Field label="หมายเหตุ (ถ้ามี)">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
          </Field>
        )}
        <Button block loading={busy} disabled={!ready} onClick={() => void submit()}>บันทึกการจอง</Button>
      </Page>
      <NeedsConfirmDialog details={needs} loading={busy} onCancel={() => setNeeds(null)}
        onConfirm={() => {
          const flags = { confirmOutsideHours: needs?.outsideHours === true, confirmTimeOff: needs?.timeOff === true };
          setNeeds(null);
          void submit(flags);
        }} />
      <StaffTabs owner={isOwner} />
    </>
  );
}
